/**
 * ProfilePage — full social/dashboard profile.
 * Web port of client-native/src/features/profile/screens/ProfileScreen.tsx.
 *
 * Sky background + header (settings/share) + large editable avatar w/ picker +
 * editable bio + 3-stat row (tap → StatPopup) + Analytics/Liked/History tabs.
 * Drops/trees are erased (PWA). Settings opens inline (web single-column).
 */
import React, { useEffect, useState } from "react";
import { Settings as SettingsIcon, Share as ShareIcon } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import { PageContainer, colors } from "@/ui";
import { getUserStats, callWithAuth, getUserInfo } from "@/api/Api";
import ContinueButton from "@/components/Buttons/ContinueButton";
import { useNavigate, useSearchParams } from "react-router-dom";
import Settings from "../components/Profile/Settings";
import { useUserSettings } from "@/hooks/useUserSettings";
import { useDisplayLevel } from "@/hooks/useDisplayLevel";
import AppBackground from "../components/AppBackground";
import { LoadingPage } from "./LoadingPage";
import {
  useTrackProfilePageViewed,
  useTrackProfileActions,
} from "@/hooks/posthog/userTrackProfilePage";

import ProfileAvatar from "../components/Profile/ProfileAvatar";
import ProfileBio from "../components/Profile/ProfileBio";
import ProfileStats, { StatType } from "../components/Profile/ProfileStats";
import ProfileTabs, { ProfileTabType } from "../components/Profile/ProfileTabs";
import AnalyticsTabContent from "../components/Profile/AnalyticsTabContent";
import LikedTabContent from "../components/Profile/LikedTabContent";
import WatchHistoryTabContent from "../components/Profile/WatchHistoryTabContent";
import StatPopup from "../components/Profile/StatPopup";
import ProfilePicturePicker from "../components/Profile/ProfilePicturePicker";
import { shareProfileStats } from "../components/Profile/profileShare";
import { formatPopupListening } from "../components/Profile/profileFormat";
import {
  BirdAvatarKey,
  ProfileAvatarType,
  getProfileAvatarTypeKey,
  getProfileAvatarValueKey,
  getDefaultBirdForName,
  getBirdAvatarSource,
  readStoredAvatar,
} from "../components/Profile/avatarUtils";

const ProfilePage: React.FC = () => {
  const nav = useNavigate();
  const posthog = usePostHog();
  // Settings-open lives in the URL (?settings=1) rather than local state so that
  // navigating to a full-page route from inside Settings (Privacy / Terms /
  // Support) and pressing that page's back button returns to Settings instead of
  // unwinding to the profile/home. Opening pushes a history entry so back lands
  // here; closing replaces so there's no dangling ?settings=1 to back into.
  const [searchParams, setSearchParams] = useSearchParams();
  const showSettings = searchParams.get("settings") === "1";
  const setShowSettings = (open: boolean) => {
    setSearchParams(
      (prev) => {
        if (open) prev.set("settings", "1");
        else prev.delete("settings");
        return prev;
      },
      { replace: !open }
    );
  };
  const [activeTab, setActiveTab] = useState<ProfileTabType>("analytics");
  const [statPopup, setStatPopup] = useState<StatType | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [avatarType, setAvatarType] = useState<ProfileAvatarType>("bird");
  const [avatarValue, setAvatarValue] = useState<string | null>(null);

  useUserSettings();
  useTrackProfilePageViewed();
  const {
    trackSettingsClicked,
    trackLogoutClicked,
    trackDeleteAccountClicked,
    trackManageBillingClicked,
  } = useTrackProfileActions();

  const userInfoQuery = useQuery({
    queryKey: ["userInfo"],
    queryFn: () => callWithAuth(getUserInfo),
  });
  // iOS reads live globalStore counters, so the profile always shows fresh
  // stats. The global QueryClient disables refetchOnMount, so opt this screen
  // back in: background-refetch the stats every time the profile is opened.
  const userStatsQuery = useQuery({
    queryKey: ["userStats"],
    queryFn: () => callWithAuth(getUserStats),
    refetchOnMount: "always",
  });
  // Activation-aware iOS display level (useCurrentLevel port) — iOS never
  // shows the server's hours-only userStats.level.
  const { level } = useDisplayLevel();

  const user = userInfoQuery.data;
  const userId = user?.id;

  // Load avatar preference (per-user, validated) or assign default bird by name.
  // Goes through readStoredAvatar so Profile and chat normalize stale/corrupt
  // storage identically.
  useEffect(() => {
    if (!userId) return;
    const stored = readStoredAvatar(userId);
    if (stored) {
      setAvatarType(stored.type);
      setAvatarValue(stored.value);
    } else {
      setAvatarType("bird");
      setAvatarValue(getDefaultBirdForName(user?.firstName));
    }
  }, [userId, user?.firstName]);

  if (userInfoQuery.isPending || userStatsQuery.isPending) {
    return (
      <div className="fixed inset-2 flex items-center justify-center">
        <LoadingPage showText={false} />
      </div>
    );
  }

  if (userInfoQuery.isError || userStatsQuery.isError) {
    return (
      <>
        <AppBackground variant="fade" />
        <div
          style={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            height: "100%",
            padding: 24,
            textAlign: "center",
            color: "#fff",
          }}
        >
          <span aria-hidden style={{ fontSize: 48 }}>
            📡
          </span>
          <span
            style={{ fontSize: "var(--base-font-size-large)", fontWeight: 700 }}
          >
            You're offline
          </span>
          <span
            style={{ fontSize: "var(--base-font-size-small)", opacity: 0.9 }}
          >
            Check your connection and try again
          </span>
          <button
            type="button"
            onClick={() => {
              userInfoQuery.refetch();
              userStatsQuery.refetch();
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "10px 24px",
              borderRadius: 999,
              background: "#ffffff",
              color: "#0966BF",
              border: "none",
              cursor: "pointer",
              fontWeight: 700,
            }}
          >
            <span aria-hidden>🔄</span> Tap to retry
          </button>
        </div>
      </>
    );
  }

  const listeningTime = userStatsQuery.data?.listeningTime || 0;
  const wordsKnown = userStatsQuery.data?.wordsKnown || 0;
  // Show the TRUE streak, including 0 for new/lapsed users. `|| 1` (which iOS
  // also uses) turned a real 0-day streak into "1 day" because 0 is falsy; `?? 0`
  // only falls back when the stat is missing (null/undefined). Deliberate
  // divergence from iOS, which misreports 0 as 1.
  const dayStreak = userStatsQuery.data?.dayStreak ?? 0;
  const reviewedCards = userStatsQuery.data?.reviewedCardsCount || 0;
  const completedLessons = userStatsQuery.data?.completedLessonsCount || 0;
  const isTemp = user?.isTemp;
  const weeksInARow = Math.floor(dayStreak / 7);

  const persistAvatar = (type: ProfileAvatarType, value: string) => {
    if (!userId) return;
    // Guard against a localStorage quota error (a large custom-image data URL)
    // so a failed write can't throw out of the picker. The image is already
    // downscaled in ProfilePicturePicker; this is a belt-and-suspenders catch.
    try {
      localStorage.setItem(getProfileAvatarTypeKey(userId), type);
      localStorage.setItem(getProfileAvatarValueKey(userId), value);
    } catch (err) {
      console.error("[ProfilePage] failed to persist avatar (quota?):", err);
    }
  };
  const handleSelectBird = (key: BirdAvatarKey) => {
    setAvatarType("bird");
    setAvatarValue(key);
    persistAvatar("bird", key);
  };
  const handleSelectCustomImage = (dataUrl: string) => {
    setAvatarType("custom");
    setAvatarValue(dataUrl);
    persistAvatar("custom", dataUrl);
  };

  const avatarSrc =
    avatarValue == null
      ? undefined
      : avatarType === "bird"
        ? getBirdAvatarSource(avatarValue as BirdAvatarKey)
        : avatarValue;

  const handleShare = () => shareProfileStats(user?.authID, posthog);

  const popupValue = (() => {
    switch (statPopup) {
      case "listening":
        return formatPopupListening(listeningTime);
      case "words":
        return wordsKnown >= 1000
          ? `${(wordsKnown / 1000).toFixed(1)}K`
          : wordsKnown.toString();
      case "streak":
        return `${dayStreak} days`;
      default:
        return "";
    }
  })();

  if (showSettings) {
    return (
      <PageContainer>
        {/* iOS SettingsSubPageLayout uses ~12px top padding; the prior pt-16
            (64px) pushed every settings title far down the screen. */}
        <div className="pt-3 pb-24">
          <Settings
            onClose={() => setShowSettings(false)}
            userEmail={user?.email ?? ""}
            onTrackDeleteAccountClicked={trackDeleteAccountClicked}
            onTrackManageBillingClicked={trackManageBillingClicked}
            onTrackLogoutClicked={trackLogoutClicked}
          />
        </div>
      </PageContainer>
    );
  }

  const avatarBlock = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
        paddingTop: 8,
      }}
    >
      <ProfileAvatar
        src={avatarSrc}
        initials={user?.firstName ? user.firstName.charAt(0).toUpperCase() : "?"}
        onPress={() => setPickerOpen(true)}
      />
      <span className="text-xl font-bold" style={{ color: colors.primary }}>
        {user?.firstName || "User"}
      </span>
      <ProfileBio />
    </div>
  );

  const statsBlock = (
    <ProfileStats
      listeningTimeSeconds={listeningTime}
      wordsKnown={wordsKnown}
      streak={dayStreak}
      onStatPress={setStatPopup}
    />
  );

  const tabsBlock = (
    <>
      <ProfileTabs value={activeTab} onChange={setActiveTab} />
      <div>
        {activeTab === "analytics" && (
          <AnalyticsTabContent
            streak={dayStreak}
            listeningTimeSeconds={listeningTime}
            wordsKnown={wordsKnown}
            level={level}
            reviewedCards={reviewedCards}
            completedLessons={completedLessons}
          />
        )}
        {activeTab === "liked" && <LikedTabContent />}
        {activeTab === "history" && <WatchHistoryTabContent />}
      </div>
    </>
  );

  const tempCta = isTemp ? (
    <div style={{ marginTop: 24 }}>
      <ContinueButton text="Login/Signup" onClick={() => nav("/signup")} />
    </div>
  ) : null;

  return (
    // Blue sky + sun + clouds + grass behind the content (matches iOS Profile).
    // Only Courses is a solid background.
    <>
      <AppBackground variant="fade" />

      {/* Settings + share — pinned to the top-left of the centered content column
          (max-600), so they sit with the content. Fixed so they stay put while
          the profile scrolls. */}
      <div
        style={{
          position: "fixed",
          top: 14,
          left: "50%",
          transform: "translateX(-50%)",
          width: "100%",
          maxWidth: 600,
          paddingLeft: 16,
          boxSizing: "border-box",
          zIndex: 2,
          display: "flex",
          gap: 16,
          pointerEvents: "none",
        }}
      >
        <button
          type="button"
          aria-label="Settings"
          onClick={() => {
            trackSettingsClicked();
            setShowSettings(true);
          }}
          style={{ background: "none", border: "none", cursor: "pointer", lineHeight: 0, pointerEvents: "auto" }}
        >
          <SettingsIcon size={24} color={colors.primary} />
        </button>
        <button
          type="button"
          aria-label="Share"
          onClick={handleShare}
          style={{ background: "none", border: "none", cursor: "pointer", lineHeight: 0, pointerEvents: "auto" }}
        >
          <ShareIcon size={24} color={colors.primary} />
        </button>
      </div>

      <PageContainer padded={false}>
        <div
          // No horizontal padding here: the page column already insets the
          // content 16px (same edge the fixed gear/share header sits at). The
          // extra paddingLeft/Right pushed the cards 16px further in than the
          // header, so the gear read as "too far left" vs the content.
          style={{ position: "relative", zIndex: 1, paddingTop: 56 }}
          className="pb-24"
        >
          {avatarBlock}
          {statsBlock}
          {tabsBlock}
          {tempCta}
        </div>
      </PageContainer>

      <StatPopup
        open={statPopup !== null}
        statType={statPopup}
        value={popupValue}
        weeksInARow={weeksInARow}
        onClose={() => setStatPopup(null)}
        onShare={() => {
          setStatPopup(null);
          setTimeout(handleShare, 250);
        }}
      />
      <ProfilePicturePicker
        open={pickerOpen}
        selectedBird={avatarType === "bird" ? (avatarValue as BirdAvatarKey) : null}
        onClose={() => setPickerOpen(false)}
        onSelectBird={handleSelectBird}
        onSelectCustomImage={handleSelectCustomImage}
      />
    </>
  );
};

export default ProfilePage;
